import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { auth, firestore } from "../firebase";
import useAuth from "../hooks/useAuth";
import TopBar from "./TopBar";
import Modal from "./Modal";
import { useModal } from "../hooks/useModal";
import { FiUsers } from "react-icons/fi";
import "../styles/quotation-system.css";

function computeCodePrefix(name) {
  if (!name) return "XX";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return (parts[0].slice(0, 2)).toUpperCase();
}

export default function UserManagement() {
  const history = useHistory();
  const { profile, user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "sales" });
  const [users, setUsers] = useState([]);
  const { modal, showAlert, showConfirm, hideModal } = useModal();

  useEffect(() => {
    document.body.classList.add('quotation-page');
    return () => {
      document.body.classList.remove('quotation-page');
    };
  }, []);

  useEffect(() => {
    if (profile?.role !== "admin") return;
    if (!user?.uid) return;
    
    // FIX: Prevent crashes and redirect loops by handling errors gracefully
    // Don't show alerts that might cause state updates during render
    
    let isMounted = true;
    let unsub = null;
    let hasLoadedOnce = false;
    
    const loadUsers = async () => {
      try {
        // First, verify we can read our own document (this should always work)
        const currentUserDoc = await firestore.collection("users").doc(user.uid).get();
        
        if (!currentUserDoc.exists) {
          console.error("Current user document does not exist in Firestore!");
          return;
        }
        
        const userData = currentUserDoc.data();
        if (userData.role !== 'admin') {
          console.error("Current user is not an admin! Role:", userData.role);
          return;
        }
        
        // Try to load users - use get() instead of onSnapshot to avoid permission issues
        try {
          const snap = await firestore.collection("users").get();
          const usersList = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((u) => u.role !== "customer"); // Exclude customers - they're managed in OPEX Customers page
          
          // Sort client-side
          usersList.sort((a, b) => {
            if (a.createdAt && b.createdAt) {
              return (b.createdAt || 0) - (a.createdAt || 0);
            }
            return (a.email || "").localeCompare(b.email || "");
          });
          
          if (isMounted) {
            setUsers(usersList);
            hasLoadedOnce = true;
            console.log("Users loaded successfully:", usersList.length);
          }
          
          // Set up real-time listener with error handling that won't crash
          // Only set up listener if initial load succeeded
          if (isMounted && hasLoadedOnce) {
            unsub = firestore.collection("users").onSnapshot(
              (snap) => {
                // Use setTimeout to prevent state updates during render
                setTimeout(() => {
                  if (isMounted) {
                    try {
                      const usersList = snap.docs
                        .map((d) => ({ id: d.id, ...d.data() }))
                        .filter((u) => u.role !== "customer"); // Exclude customers - they're managed in OPEX Customers page
                      usersList.sort((a, b) => {
                        if (a.createdAt && b.createdAt) {
                          return (b.createdAt || 0) - (a.createdAt || 0);
                        }
                        return (a.email || "").localeCompare(b.email || "");
                      });
                      setUsers(usersList);
                    } catch (err) {
                      console.warn("Error updating users list:", err);
                    }
                  }
                }, 0);
              },
              (error) => {
                // Silently handle listener errors - don't crash or show alerts
                console.warn("Snapshot listener error (using cached data):", error.code);
                // Don't unset users - keep the last successfully loaded list
              }
            );
          }
        } catch (getError) {
          console.error("Failed to load users:", getError.code, getError.message);
          // Don't show alerts - just log the error
          // This prevents React errors and redirect loops
          if (!hasLoadedOnce && isMounted) {
            // Only set empty array if we've never loaded successfully
            setUsers([]);
          }
        }
      } catch (error) {
        console.error("Error in loadUsers:", error);
        // Don't show alerts or update state during error handling
        // This prevents React error #185 (state update during render)
      }
    };
    
    // Use setTimeout to ensure this runs after render completes
    const timeoutId = setTimeout(() => {
      loadUsers();
    }, 0);
    
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      if (unsub) {
        try {
          unsub();
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, [profile, user]);

  const createUser = async () => {
    if (profile?.role !== "admin") return;
    if (!form.email || !form.name) {
      showAlert("Name and email are required.", "warning", "Required Fields");
      return;
    }
    
    // Check if email already exists as a customer
    try {
      const emailLower = form.email.trim().toLowerCase();
      const customersSnapshot = await firestore
        .collection("opexCustomers")
        .where("email", "==", emailLower)
        .limit(1)
        .get();
      
      if (!customersSnapshot.empty) {
        showAlert(
          `An account with email "${form.email}" already exists as a customer. Please use a different email address.`,
          "error",
          "Email Already Exists"
        );
        return;
      }
    } catch (error) {
      console.error("Error checking customer email:", error);
      // Continue with creation if check fails (don't block user)
    }
    
    // CRITICAL FIX: Save current admin info before creating new user
    // createUserWithEmailAndPassword automatically signs in as the new user,
    // which causes the admin to be logged out. We'll handle this gracefully.
    const currentAdmin = auth.currentUser;
    const currentAdminEmail = currentAdmin?.email;
    const currentAdminUid = currentAdmin?.uid;
    
    if (!currentAdmin || currentAdminEmail !== "ronak@lunarelectric.in") {
      showAlert("Error: Admin session not found. Please log in again.", "error", "Session Error");
      return;
    }
    
    try {
      const password = form.password || Math.random().toString(36).slice(-8);
      const codePrefix = computeCodePrefix(form.name);
      
      // Create Auth user - WARNING: This will sign in as the new user automatically
      const cred = await auth.createUserWithEmailAndPassword(form.email.trim(), password);
      const newUserId = cred.user.uid;
      
      // Determine role - check if there's a role field in form, default to sales
      const userRole = form.role || "sales";
      
      // Immediately create the Firestore profile
      await firestore.collection("users").doc(newUserId).set({
        name: form.name,
        email: form.email,
        phone: form.phone || "",
        role: userRole,
        codePrefix: userRole === "sales" ? codePrefix : "",
        createdAt: Date.now(),
      });
      
      // CRITICAL: We're now signed in as the new user. We need to sign out
      // and the AuthContext will detect the change. However, we can't automatically
      // sign back in without the password. The best approach is to:
      // 1. Sign out immediately
      // 2. Store a flag that we just created a user
      // 3. Let the user know they need to refresh/re-login
      
      await auth.signOut();
      
      // Store info about the created user in sessionStorage
      sessionStorage.setItem('just_created_user', JSON.stringify({
        email: form.email,
        password: password,
        codePrefix: codePrefix,
        timestamp: Date.now()
      }));
      
      // Show success message with instructions
      const roleLabel = userRole === "contentwriter" ? "Content Writer" : "Sales Executive";
      const successMsg = `${roleLabel} created successfully!\n\nEmail: ${form.email}\nPassword: ${password}${userRole === "sales" ? `\nCode: ${codePrefix}` : ""}\n\nNote: Creating a user signs you in as that user. Please refresh the page and log in again as admin.`;
      showAlert(successMsg, "success", "User Created");
      
      // Clear form before redirecting
      setForm({ name: "", email: "", phone: "", password: "" });
      
      // Redirect to login - the user will need to log in again
      // The sessionStorage flag can be used to show a helpful message on login page
      window.location.href = "/login";
      
    } catch (error) {
      console.error("Error creating user:", error);
      
      // Check if we're still signed in as admin
      const stillAdmin = auth.currentUser?.uid === currentAdminUid;
      
      if (stillAdmin) {
        // Still admin, show error and stay on page
        if (error.code === 'auth/email-already-in-use') {
          showAlert("This email is already registered. Please use a different email.", "error", "Email Exists");
        } else {
          showAlert("Error creating user: " + (error.message || "Unknown error"), "error", "Error");
        }
      } else {
        // Auth state changed unexpectedly
        console.error("Auth state changed during user creation");
        showAlert("Session was interrupted. Please log in again.", "error", "Session Error");
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      }
    }
  };

  const deleteUser = async (userId, userEmail, userRole) => {
    if (profile?.role !== "admin") return;
    if (userRole === "admin") {
      showAlert("Cannot delete admin users.", "warning", "Cannot Delete");
      return;
    }
    showConfirm(
      `Delete user ${userEmail}? This will remove their profile. Note: You'll need to manually delete their Auth account from Firebase Console.`,
      async () => {
        try {
          await firestore.collection("users").doc(userId).delete();
          showAlert("User profile deleted. Remember to delete their Auth account from Firebase Console if needed.", "success", "User Deleted");
        } catch (e) {
          showAlert("Error deleting user: " + e.message, "error", "Error");
        }
      },
      "Delete User",
      "warning"
    );
  };

  return (
    <div className="quotation-system">
      <TopBar />
      <Modal {...modal} onClose={hideModal} />
      <div className="elegant-container fade-in-up">
        {/* List of existing users */}
        <div className="elegant-card" style={{ marginBottom: "2rem" }}>
          <h1 className="elegant-title">User Management</h1>
          <p className="elegant-subtitle">Manage sales executives, content writers, and user accounts</p>
          
          {users.length === 0 ? (
            <div style={{ textAlign: "center", padding: "3rem 2rem" }}>
              <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "center" }}>
                <FiUsers style={{ fontSize: "3rem", color: "var(--accent-purple)" }} />
              </div>
              <p style={{ color: "var(--neutral-600)" }}>No users found.</p>
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table className="table-elegant">
                  <thead>
                    <tr>
                      <th align="left">Name</th>
                      <th align="left">Email</th>
                      <th align="left">Phone</th>
                      <th align="left">Role</th>
                      <th align="left">Code Prefix</th>
                      <th align="left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr 
                        key={u.id}
                        onClick={() => history.push(`/admin/users/${u.id}`)}
                        style={{ cursor: "pointer" }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(139, 92, 246, 0.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "";
                        }}
                      >
                        <td data-label="Name" style={{ fontWeight: "600" }}>{u.name || "-"}</td>
                        <td data-label="Email">{u.email || "-"}</td>
                        <td data-label="Phone">{u.phone || "-"}</td>
                        <td data-label="Role">
                          <span className={`badge-elegant ${
                            u.role === "admin" ? "badge-elegant-gold" : 
                            u.role === "contentwriter" ? "badge-elegant-secondary" : 
                            "badge-elegant-primary"
                          }`}>
                            {u.role === "contentwriter" ? "Content Writer" : u.role || "-"}
                          </span>
                        </td>
                        <td data-label="Code Prefix">
                          <span className="badge-elegant badge-elegant-primary">{u.codePrefix || (u.role === "contentwriter" ? "N/A" : "-")}</span>
                        </td>
                        <td data-label="Actions" onClick={(e) => e.stopPropagation()}>
                          {u.role === "admin" ? (
                            <span style={{ color: "var(--neutral-500)", fontSize: "0.875rem", fontWeight: "500" }}>Protected</span>
                          ) : (
                            <button
                              onClick={() => deleteUser(u.id, u.email, u.role)}
                              className="btn-elegant btn-elegant-danger"
                              style={{ padding: "0.5rem 1rem", fontSize: "0.875rem", width: "100%", minHeight: "44px" }}
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ marginTop: "1rem", fontSize: "0.875rem", color: "var(--neutral-500)", fontStyle: "italic" }}>
                Note: Passwords cannot be retrieved for security reasons. To reset a password, delete and recreate the user, or use Firebase Console.
              </p>
            </>
          )}
        </div>

        {/* Create new user form */}
        <div className="elegant-card">
          <h2 className="elegant-section-title">Create New User</h2>
          <p className="elegant-subtitle" style={{ marginBottom: "2rem" }}>
            Create new users. Share the generated password with the user.
          </p>
          <div style={{ maxWidth: "600px" }}>
            <div className="form-group-elegant">
              <label className="form-label-elegant">Full Name</label>
              <input 
                className="form-input-elegant"
                value={form.name} 
                onChange={(e) => setForm({ ...form, name: e.target.value })} 
                placeholder="John Doe"
              />
            </div>
            <div className="form-group-elegant">
              <label className="form-label-elegant">Email Address</label>
              <input 
                type="email"
                className="form-input-elegant"
                value={form.email} 
                onChange={(e) => setForm({ ...form, email: e.target.value })} 
                placeholder="john.doe@example.com"
              />
            </div>
            <div className="form-group-elegant">
              <label className="form-label-elegant">Role *</label>
              <select
                className="form-input-elegant"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <option value="sales">Sales Executive</option>
                <option value="contentwriter">Content Writer</option>
              </select>
            </div>
            <div className="form-group-elegant">
              <label className="form-label-elegant">Phone Number</label>
              <input 
                className="form-input-elegant"
                value={form.phone} 
                onChange={(e) => setForm({ ...form, phone: e.target.value })} 
                placeholder="+91 9876543210"
              />
            </div>
            <div className="form-group-elegant">
              <label className="form-label-elegant">Temporary Password (optional)</label>
              <input 
                type="password"
                className="form-input-elegant"
                value={form.password} 
                onChange={(e) => setForm({ ...form, password: e.target.value })} 
                placeholder="Leave empty to auto-generate"
              />
              <p style={{ marginTop: "0.5rem", fontSize: "0.75rem", color: "var(--neutral-500)" }}>
                If left empty, a secure password will be automatically generated
              </p>
            </div>
            <button 
              onClick={createUser}
              className="btn-elegant btn-elegant-accent"
              style={{ width: "100%", marginTop: "0.5rem" }}
            >
              Create User
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


